import React, { useState, useMemo } from 'react';
import { Button, Input, Select, Switch, Tabs, Avatar } from 'antd';
import {
  UsergroupAddOutlined,
  CreditCardOutlined,
  BellOutlined,
  SafetyOutlined ,
  PlusOutlined,
  SettingOutlined,
  EyeOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './Configuration.scss';

const { Option } = Select;
const { Search } = Input;

// Reusable Role Card Component
const RoleCard = ({ title, description, users, variant = 'default', onViewDetails }) => {
  const getCardClass = () => {
    switch (variant) {
      case 'admin': return 'role-card-admin';
      case 'finance': return 'role-card-finance';
      case 'reconciliation': return 'role-card-reconciliation';
      case 'support': return 'role-card-support';
      default: return 'role-card-default';
    }
  };

  return (
    <div className={`role-card ${getCardClass()}`}>
      <div className="role-card-header">
        <h4 className="role-card-title">{title}</h4>
        <UsergroupAddOutlined className="role-card-icon" />
      </div>
      <p className="role-card-description">{description}</p>
      <div className="role-card-footer">
        <span className="role-card-users">{users} users</span>
        <Button type="link" className="role-card-link" onClick={onViewDetails}>
          View Details
        </Button>
      </div>
    </div>
  );
};

// Reusable Form Row Component
const FormRow = ({ label, children, fullWidth = false }) => {
  return (
    <div className={`form-row ${fullWidth ? 'full-width' : ''}`}>
      <label className="form-label">{label}</label>
      <div className="form-input">{children}</div>
    </div>
  );
};

// Reusable Setting Item Component
const SettingItem = ({ title, description, checked, onChange }) => {
  return (
    <div className="setting-item">
      <div className="setting-item-content">
        <h4 className="setting-item-title">{title}</h4>
        <p className="setting-item-description">{description}</p>
      </div>
      <Switch checked={checked} onChange={onChange} size="default" />
    </div>
  );
};

// Reusable IP Whitelist Item Component
const IPWhitelistItem = ({ ip, onRemove }) => {
  return (
    <div className="ip-whitelist-item">
      <span className="ip-address">{ip}</span>
      <Button type="link" danger onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
};

// Main Component
const Configuration = () => {
  const [activeSection, setActiveSection] = useState('user-role');
  const [activeVCCTab, setActiveVCCTab] = useState('general');

  // User table data
  const userData = [
    {
      name: 'Rajesh Kumar',
      email: 'rajesh.kumar@company.com',
      role: 'Admin',
      status: 'active',
      lastLogin: '2 hours ago',
      avatar: 'R',
      avatarColor: '#5b8def',
    },
    {
      name: 'Priya Sharma',
      email: 'priya.sharma@company.com',
      role: 'Finance Manager',
      status: 'active',
      lastLogin: '5 hours ago',
      avatar: 'P',
      avatarColor: '#7d6feb',
    },
    {
      name: 'Amit Patel',
      email: 'amit.patel@company.com',
      role: 'Reconciliation Analyst',
      status: 'active',
      lastLogin: '1 day ago',
      avatar: 'A',
      avatarColor: '#4ecb73',
    },
  ];

  const columnDefs = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'User',
        width: 250,
        cellRenderer: (params) => (
          <div className="user-cell">
            <Avatar
              style={{ backgroundColor: params.data.avatarColor }}
              size={40}
            >
              {params.data.avatar}
            </Avatar>
            <span className="user-name">{params.value}</span>
          </div>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 280,
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 200,
        cellRenderer: (params) => {
          const getRoleClass = (role) => {
            if (role === 'Admin') return 'role-tag-admin';
            if (role === 'Finance Manager') return 'role-tag-finance';
            return 'role-tag-default';
          };
          return (
            <span className={`role-tag ${getRoleClass(params.value)}`}>
              {params.value}
            </span>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        cellRenderer: (params) => (
          <span className={`status-tag status-${params.value}`}>
            {params.value}
          </span>
        ),
      },
      {
        field: 'lastLogin',
        headerName: 'Last Login',
        width: 150,
      },
      {
        headerName: 'Actions',
        width: 120,
        cellRenderer: () => (
          <div className="action-icons">
            <Button type="text" icon={<EyeOutlined />} size="small" />
            <Button type="text" icon={<EditOutlined />} size="small" />
          </div>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'user-role':
        return (
          <div className="section-content">
            <div className="section-header">
              <div>
                <h2 className="section-title">User & Role Management</h2>
                <p className="section-subtitle">Manage users, roles, and permissions</p>
              </div>
              <div className="section-actions">
                <Button icon={<SettingOutlined />} size="large">
                  Manage Roles
                </Button>
                <Button type="primary" icon={<PlusOutlined />} size="large">
                  Add New User
                </Button>
              </div>
            </div>

            {/* Roles Overview */}
            <div className="subsection">
              <h3 className="subsection-title">Roles Overview</h3>
              <div className="roles-grid">
                <RoleCard
                  title="Admin"
                  description="Full system access with all permissions"
                  users={3}
                  variant="admin"
                  onViewDetails={() => {}}
                />
                <RoleCard
                  title="Finance Manager"
                  description="Reconciliation and reporting with approval rights"
                  users={5}
                  variant="finance"
                  onViewDetails={() => {}}
                />
                <RoleCard
                  title="Reconciliation Analyst"
                  description="View and reconciliation operations only"
                  users={8}
                  variant="reconciliation"
                  onViewDetails={() => {}}
                />
                <RoleCard
                  title="Support"
                  description="Limited access for customer support"
                  users={4}
                  variant="support"
                  onViewDetails={() => {}}
                />
              </div>
            </div>

            {/* All Users Table */}
            <div className="subsection">
              <div className="users-table-header">
                <h3 className="subsection-title">All Users</h3>
                <div className="users-filters">
                  <Search
                    placeholder="Search users..."
                    style={{ width: 300 }}
                    size="large"
                  />
                  <Select defaultValue="all-roles" size="large" style={{ width: 180 }}>
                    <Option value="all-roles">All Roles</Option>
                    <Option value="admin">Admin</Option>
                    <Option value="finance">Finance Manager</Option>
                  </Select>
                  <Select defaultValue="all-status" size="large" style={{ width: 180 }}>
                    <Option value="all-status">All Status</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </div>
              </div>

              <div className="users-table">
                <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                  <AgGridReact
                    rowData={userData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows={true}
                    pagination={false}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'vcc-rules':
        return (
          <div className="section-content">
            <div className="section-header">
              <div>
                <h2 className="section-title">VCC Rules & Controls</h2>
                <p className="section-subtitle">
                  Configure VCC generation rules, limits, and merchant controls
                </p>
              </div>
              <Button type="primary" icon={<PlusOutlined />} size="large">
                Add New Rule
              </Button>
            </div>

            <Tabs
              activeKey={activeVCCTab}
              onChange={setActiveVCCTab}
              className="vcc-tabs"
              items={[
                { key: 'general', label: 'General Settings' },
                { key: 'rules', label: 'VCC Rules' },
                { key: 'fraud', label: 'Fraud Control & Prevention' },
                { key: 'merchant', label: 'Merchant Controls' },
              ]}
            />

            <div className="vcc-tab-content">
              {activeVCCTab === 'general' && (
                <>
                  <div className="subsection">
                    <h3 className="subsection-title">Default VCC Settings</h3>
                    <div className="form-grid">
                      <FormRow label="Auto-Generation Mode">
                        <Select defaultValue="auto-all" size="large">
                          <Option value="auto-all">Auto-generate for all bookings</Option>
                          <Option value="manual">Manual generation only</Option>
                          <Option value="conditional">Conditional generation</Option>
                        </Select>
                      </FormRow>
                      <FormRow label="Card Type">
                        <Select defaultValue="mastercard" size="large">
                          <Option value="mastercard">Mastercard</Option>
                          <Option value="visa">Visa</Option>
                        </Select>
                      </FormRow>
                      <FormRow label="Maximum VCC Amount">
                        <Input prefix="₹" defaultValue="500000" size="large" />
                      </FormRow>
                      <FormRow label="Minimum VCC Amount">
                        <Input prefix="₹" defaultValue="100" size="large" />
                      </FormRow>
                    </div>
                  </div>

                  <div className="subsection">
                    <h3 className="subsection-title">Charge Window</h3>
                    <div className="form-grid">
                      <FormRow label="VCC Activation From">
                        <Select defaultValue="immediate" size="large">
                          <Option value="immediate">Immediately after creation</Option>
                          <Option value="checkin">At check-in</Option>
                          <Option value="custom">Custom time</Option>
                        </Select>
                      </FormRow>
                      <FormRow label="VCC Usage Type">
                        <Select defaultValue="multi-5" size="large">
                          <Option value="single">Single Use</Option>
                          <Option value="multi-5">Multi Use (5 attempts)</Option>
                          <Option value="unlimited">Unlimited</Option>
                        </Select>
                      </FormRow>
                      <FormRow label="Max Charge Attempts (for Multi Use)" fullWidth>
                        <Input defaultValue="3" size="large" type="number" />
                      </FormRow>
                    </div>
                  </div>

                  <div className="subsection">
                    <h3 className="subsection-title">Auto-Close / Expiry Settings</h3>
                    <div className="form-grid">
                      <FormRow label="VCC Closure After">
                        <Select defaultValue="1-day" size="large">
                          <Option value="immediate">Immediately after checkout</Option>
                          <Option value="1-day">1 day post check-out</Option>
                          <Option value="3-days">3 days post check-out</Option>
                          <Option value="7-days">7 days post check-out</Option>
                        </Select>
                      </FormRow>
                      <FormRow label="Auto-Close When">
                        <Select defaultValue="fully-charged" size="large">
                          <Option value="fully-charged">Fully charged (100%)</Option>
                          <Option value="partial">Partially charged (80%+)</Option>
                          <Option value="never">Never auto-close</Option>
                        </Select>
                      </FormRow>
                    </div>
                  </div>

                  <div className="subsection">
                    <h3 className="subsection-title">Security Settings</h3>
                    <SettingItem
                      title="Enable 3D Secure Authentication"
                      description="Recommended for international transactions"
                      checked={true}
                      onChange={() => {}}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="section-content">
            <div className="section-header">
              <div>
                <h2 className="section-title">Email Notifications</h2>
              </div>
            </div>

            <div className="subsection">
              <div className="settings-list">
                <SettingItem
                  title="VCC Expiry Alerts"
                  description="Alert 48 hours before VCC expiration"
                  checked={true}
                  onChange={() => {}}
                />
                <SettingItem
                  title="Double Payment Detection"
                  description="Immediate alert when duplicate charge detected"
                  checked={true}
                  onChange={() => {}}
                />
                <SettingItem
                  title="Reconciliation Variances"
                  description="Daily summary of reconciliation mismatches"
                  checked={true}
                  onChange={() => {}}
                />
                <SettingItem
                  title="Daily Transaction Summary"
                  description="Send at 9:00 AM every day"
                  checked={true}
                  onChange={() => {}}
                />
                <SettingItem
                  title="Weekly Performance Report"
                  description="Send every Monday at 8:00 AM"
                  checked={false}
                  onChange={() => {}}
                />
                <SettingItem
                  title="Monthly Cashback Report"
                  description="Send on 1st of every month"
                  checked={true}
                  onChange={() => {}}
                />
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="section-content">
            <div className="section-header">
              <div>
                <h2 className="section-title">Security & Audit</h2>
              </div>
            </div>

            <div className="subsection">
              <h3 className="subsection-title">Security Policies</h3>
              <div className="form-grid">
                <FormRow label="Session Timeout">
                  <Select defaultValue="15" size="large">
                    <Option value="5">5 minutes</Option>
                    <Option value="15">15 minutes</Option>
                    <Option value="30">30 minutes</Option>
                    <Option value="60">1 hour</Option>
                  </Select>
                </FormRow>
                <FormRow label="2FA Requirement">
                  <Select defaultValue="all" size="large">
                    <Option value="all">Required for all users</Option>
                    <Option value="admin">Admin only</Option>
                    <Option value="optional">Optional</Option>
                  </Select>
                </FormRow>
                <FormRow label="Password Expiry">
                  <Select defaultValue="never" size="large">
                    <Option value="30">30 days</Option>
                    <Option value="60">60 days</Option>
                    <Option value="90">90 days</Option>
                    <Option value="never">Never</Option>
                  </Select>
                </FormRow>
                <FormRow label="Failed Login Attempts">
                  <Select defaultValue="3" size="large">
                    <Option value="3">Lock after 3 attempts</Option>
                    <Option value="5">Lock after 5 attempts</Option>
                    <Option value="10">Lock after 10 attempts</Option>
                  </Select>
                </FormRow>
              </div>
            </div>

            <div className="subsection">
              <h3 className="subsection-title">Audit & Compliance</h3>
              <div className="audit-settings">
                <div className="audit-setting-row">
                  <FormRow label="Audit Log Retention Period" fullWidth>
                    <Select defaultValue="90" size="large" style={{ width: 200 }}>
                      <Option value="30">30 days</Option>
                      <Option value="60">60 days</Option>
                      <Option value="90">90 days</Option>
                      <Option value="180">180 days</Option>
                      <Option value="365">1 year</Option>
                    </Select>
                  </FormRow>
                </div>

                <SettingItem
                  title="Enable Detailed Audit Logging"
                  description="Log all user actions and system changes"
                  checked={true}
                  onChange={() => {}}
                />

                <div className="audit-action">
                  <Button size="large" block>
                    View Audit Logs
                  </Button>
                </div>
              </div>
            </div>

            <div className="subsection">
              <h3 className="subsection-title">IP Whitelist</h3>
              <div className="ip-whitelist-section">
                <div className="ip-input-group">
                  <Input
                    placeholder="Enter IP address (e.g., 192.168.1.1)"
                    size="large"
                  />
                  <Button type="primary" size="large">
                    Add IP
                  </Button>
                </div>
                <div className="ip-list">
                  <IPWhitelistItem ip="192.168.1.100" onRemove={() => {}} />
                  <IPWhitelistItem ip="10.0.0.50" onRemove={() => {}} />
                  <IPWhitelistItem ip="172.16.0.25" onRemove={() => {}} />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="configuration-page-wrapper">
      <div className="configuration-container">
        <h1 className="page-title">Configuration & Settings</h1>

        <div className="configuration-layout">
          {/* Sidebar */}
          <div className="configuration-sidebar">
            <div
              className={`sidebar-item ${activeSection === 'user-role' ? 'active' : ''}`}
              onClick={() => setActiveSection('user-role')}
            >
              <UsergroupAddOutlined className="sidebar-icon" />
              <span className="sidebar-text">User & Role Management</span>
            </div>
            <div
              className={`sidebar-item ${activeSection === 'vcc-rules' ? 'active' : ''}`}
              onClick={() => setActiveSection('vcc-rules')}
            >
              <CreditCardOutlined className="sidebar-icon" />
              <span className="sidebar-text">VCC Rules & Controls</span>
            </div>
            <div
              className={`sidebar-item ${activeSection === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveSection('notifications')}
            >
              <BellOutlined className="sidebar-icon" />
              <span className="sidebar-text">Notifications</span>
            </div>
            <div
              className={`sidebar-item ${activeSection === 'security' ? 'active' : ''}`}
              onClick={() => setActiveSection('security')}
            >
              <SafetyOutlined  className="sidebar-icon" />
              <span className="sidebar-text">Security & Audit</span>
            </div>
          </div>

          {/* Content Area */}
          <div className="configuration-content">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuration;